import { z } from "zod";
import { assertProductionSafe } from "./env";
import type {
  AdminLoginContent,
  AboutContent,
  ContactPayload,
  CreateOrderPayload,
  CreateOrderResponse,
  DonationStatus,
  Event,
  HomeContent,
  ImpactContent,
  LegalContent,
  NewsletterPayload,
  NewsletterUnsubscribePayload,
  ProgramsContent,
  RecentDonor,
  Stats,
  Testimonial,
  VerifyPaymentPayload,
  VerifyPaymentResponse,
  SendOtpPayload,
  VerifyOtpPayload,
  VerifyOtpResponse,
  SendEmailCustomPayload,
} from "./api-types";
import {
  AdminLoginContentSchema,
  AboutContentSchema,
  ContactPayloadSchema,
  CreateOrderPayloadSchema,
  CreateOrderResponseSchema,
  DonationStatusSchema,
  EventSchema,
  HomeContentSchema,
  ImpactContentSchema,
  LegalContentSchema,
  NewsletterPayloadSchema,
  NewsletterUnsubscribePayloadSchema,
  ProgramsContentSchema,
  RecentDonorSchema,
  StatsSchema,
  TestimonialSchema,
  VerifyPaymentPayloadSchema,
  VerifyPaymentResponseSchema,
  SendOtpPayloadSchema,
  VerifyOtpPayloadSchema,
  VerifyOtpResponseSchema,
  SendEmailCustomPayloadSchema,
} from "./api-types";
import { parseApiErrorMessage } from "./api-error-message";
import { cmsEnabled } from "./cms-enabled";
import { mockAppRequest } from "./mock-app-request";
import { mockApi } from "./mock-api";
import {
  mergeAboutContent,
  mergeHomeContent,
  mergeImpactContent,
  mergeProgramsContent,
  withEventsListFallback,
  withTestimonialFallbacks,
} from "./site-content-fallbacks";
import { mergeLegalContent } from "./legal-content";

assertProductionSafe();

const USE_MOCK = process.env.NEXT_PUBLIC_USE_MOCK_API === "true";
const BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:3001";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  options?: RequestInit & { tags?: string[] }
): Promise<T> {
    const method = (options?.method ?? "GET").toUpperCase();
  const { tags, ...fetchOptions } = options ?? {};
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { "Content-Type": "application/json", ...fetchOptions?.headers },
    ...fetchOptions,
    ...(method === "GET"
      ? tags && tags.length > 0
        ? { next: { tags, revalidate: 60 } }
        : { cache: "no-store" as RequestCache }
      : {}),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "Request failed");
    throw new ApiError(parseApiErrorMessage(text, res.status), res.status);
  }

  return res.json() as Promise<T>;
}

export const api = {
  async submitContact(payload: ContactPayload) {
    const data = ContactPayloadSchema.parse(payload);
    if (USE_MOCK) return mockApi.submitContact(data);
    return request<{ success: boolean; id: string }>("/api/contact", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  async subscribeNewsletter(payload: NewsletterPayload) {
    const data = NewsletterPayloadSchema.parse(payload);
    if (USE_MOCK) return mockApi.subscribeNewsletter(data);
    return request<{ success: boolean }>("/api/newsletter", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  async unsubscribeNewsletter(payload: NewsletterUnsubscribePayload) {
    const data = NewsletterUnsubscribePayloadSchema.parse(payload);
    if (USE_MOCK) return mockApi.unsubscribeNewsletter(data);
    return request<{ success: boolean }>("/api/newsletter/unsubscribe", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  async getAdminLoginContent(): Promise<AdminLoginContent> {
    if (USE_MOCK) return mockApi.getAdminLoginContent();
    const data = await request<AdminLoginContent>("/api/admin-login-content");
    return AdminLoginContentSchema.parse(data);
  },

  async getStats(): Promise<Stats> {
    if (USE_MOCK) return mockApi.getStats();
    const data = await request<Stats>("/api/stats");
    return StatsSchema.parse(data);
  },

  async getEvents(): Promise<Event[]> {
    if (!cmsEnabled) return withEventsListFallback([]);
    if (USE_MOCK) {
      const data = await mockAppRequest<Event[]>("/api/events");
      return withEventsListFallback(z.array(EventSchema).parse(data));
    }
    const data = await request<Event[]>("/api/events", {
      tags: ["cms", "cms-events"],
    });
    return withEventsListFallback(z.array(EventSchema).parse(data));
  },

  async getTestimonials(): Promise<Testimonial[]> {
    if (!cmsEnabled) return withTestimonialFallbacks([]);
    if (USE_MOCK) {
      const data = await mockAppRequest<Testimonial[]>("/api/testimonials");
      return withTestimonialFallbacks(z.array(TestimonialSchema).parse(data));
    }
    const data = await request<Testimonial[]>("/api/testimonials", {
      tags: ["cms", "cms-testimonials"],
    });
    return withTestimonialFallbacks(z.array(TestimonialSchema).parse(data));
  },

  async getHomeContent(): Promise<HomeContent> {
    if (!cmsEnabled) return mergeHomeContent(undefined);
    if (USE_MOCK) {
      const data = await mockAppRequest<HomeContent>("/api/home-content");
      return mergeHomeContent(HomeContentSchema.parse(data));
    }
    const data = await request<HomeContent>("/api/home-content", {
      tags: ["cms", "cms-home"],
    });
    return mergeHomeContent(HomeContentSchema.parse(data));
  },

  async getAboutContent(): Promise<AboutContent> {
    if (!cmsEnabled) return mergeAboutContent(undefined);
    if (USE_MOCK) {
      const data = await mockAppRequest<AboutContent>("/api/about-content");
      return mergeAboutContent(AboutContentSchema.parse(data));
    }
    const data = await request<AboutContent>("/api/about-content", {
      tags: ["cms", "cms-about"],
    });
    return mergeAboutContent(AboutContentSchema.parse(data));
  },

  async getLegalContent(): Promise<LegalContent> {
    if (USE_MOCK) {
      const data = await mockAppRequest<LegalContent>("/api/legal-content");
      return mergeLegalContent(LegalContentSchema.parse(data));
    }
    const data = await request<LegalContent>("/api/legal-content", {
      tags: ["cms", "cms-legal"],
    });
    return mergeLegalContent(LegalContentSchema.parse(data));
  },

  async getProgramsContent(): Promise<ProgramsContent> {
    if (!cmsEnabled) return mergeProgramsContent(undefined);
    if (USE_MOCK) return mockApi.getProgramsContent();
    try {
      const data = await request<ProgramsContent>("/api/programs-content", {
        tags: ["cms", "cms-programs"],
      });
      return mergeProgramsContent(ProgramsContentSchema.parse(data));
    } catch {
      return mergeProgramsContent(undefined);
    }
  },

  async getImpactContent(): Promise<ImpactContent> {
    if (!cmsEnabled) return mergeImpactContent(undefined);
    if (USE_MOCK) return mockApi.getImpactContent();
    try {
      const data = await request<ImpactContent>("/api/impact-content", {
        tags: ["cms", "cms-impact"],
      });
      return mergeImpactContent(ImpactContentSchema.parse(data));
    } catch {
      return mergeImpactContent(undefined);
    }
  },

  async getRecentDonors(): Promise<RecentDonor[]> {
    if (USE_MOCK) return mockApi.getRecentDonors();
    const data = await request<RecentDonor[]>("/api/donations/recent");
    return z.array(RecentDonorSchema).parse(data);
  },

  async sendOtp(payload: SendOtpPayload) {
    const data = SendOtpPayloadSchema.parse(payload);
    if (USE_MOCK) return mockApi.sendOtp(data);
    return request<{ success: boolean; message: string }>("/api/donations/send-otp", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  async verifyOtp(payload: VerifyOtpPayload): Promise<VerifyOtpResponse> {
    const data = VerifyOtpPayloadSchema.parse(payload);
    if (USE_MOCK) return mockApi.verifyOtp(data);
    const response = await request<VerifyOtpResponse>("/api/donations/verify-otp", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return VerifyOtpResponseSchema.parse(response);
  },

  async createDonationOrder(
    payload: CreateOrderPayload
  ): Promise<CreateOrderResponse> {
    const data = CreateOrderPayloadSchema.parse(payload);
    if (USE_MOCK) return mockApi.createDonationOrder(data);
    const response = await request<CreateOrderResponse>(
      "/api/donations/create-order",
      { method: "POST", body: JSON.stringify(data) }
    );
    return CreateOrderResponseSchema.parse(response);
  },

  async verifyDonation(
    payload: VerifyPaymentPayload
  ): Promise<VerifyPaymentResponse> {
    const data = VerifyPaymentPayloadSchema.parse(payload);
    if (USE_MOCK) return mockApi.verifyDonation(data);
    const response = await request<VerifyPaymentResponse>(
      "/api/donations/verify",
      { method: "POST", body: JSON.stringify(data) }
    );
    return VerifyPaymentResponseSchema.parse(response);
  },

  async getDonationStatus(
    donationId: string,
    options: { token?: string; access?: string }
  ): Promise<DonationStatus> {
    const params = new URLSearchParams();
    if (options.token) params.set("token", options.token);
    if (options.access) params.set("access", options.access);
    if (USE_MOCK) {
      return mockApi.getDonationStatus(donationId, options);
    }
    const response = await request<DonationStatus>(
      `/api/donations/${encodeURIComponent(donationId)}/status?${params.toString()}`
    );
    return DonationStatusSchema.parse(response);
  },

  async sendDonationEmailCustom(
    donationId: string,
    payload: SendEmailCustomPayload
  ): Promise<{ success: boolean; message: string }> {
    const data = SendEmailCustomPayloadSchema.parse(payload);
    if (USE_MOCK) return mockApi.sendDonationEmailCustom(donationId, data);
    return request<{ success: boolean; message: string }>(
      `/api/donations/${encodeURIComponent(donationId)}/send-email`,
      { method: "POST", body: JSON.stringify(data) }
    );
  },
};
